import { LazyLoading } from "@/components/loaders/Loader/LazyLoading";
import { useAssetDetailsContext } from "@/pages/asset-details/context/assetDetailsContext";
import { upsertMarkDownText } from "@/pages/asset-details/repositories/AssetDetailsRepositorie";
import { Button, CircularProgress, Snackbar } from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import { createStyles, makeStyles } from "@mui/styles";
import Alert from "@mui/lab/Alert";
import MarkdownIt from "markdown-it";
import { useEffect, useMemo, useState, useRef } from "react";
import MdEditor from "react-markdown-editor-lite";
import "react-markdown-editor-lite/lib/index.css";
import { useSearchParams } from "react-router-dom";
import { useSearchMarkDownText } from "./hooks/useSearchMarkDownText";
import { ErrorModal, MESApiService } from "@dexteel/mesf-core";

const useStyles = makeStyles((theme) =>
	createStyles({
		root: {
			"& .button-type-table": {
				display: "none !important",
			},
			"& .button-type-strikethrough": {
				display: "none !important",
			},
		},
		markDownTextComponent: {
			height: "100%",
			width: "100%",
			borderRadius: "5px",
			whiteSpace: "break-spaces",
		},
		buttonContainer: {
			padding: theme.spacing(1, 0),
			minHeight: 60,
			flexShrink: 0,
		},
		buttonGrid: {
			marginBottom: theme.spacing(0.5),
		},
		editorContainer: {
			display: "flex",
			flexDirection: "column",
			height: "100%",
		},
		editorWrapper: {
			flex: 1,
			minHeight: 0,
		},
	}),
);

export const AssetTextGrid = () => {
	const [searchParams] = useSearchParams();
	const containerRef = useRef<HTMLDivElement>(null);
	const [editorHeight, setEditorHeight] = useState<number>(300);
	const mdParser = useMemo(() => new MarkdownIt(), []);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [error, setError] = useState<string>("");
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [updateMarkDownerror, setUpdateMarkDownError] = useState<string>("");
	const [editMarkDownTextSuccess, setEditMarkDownTextSuccess] = useState(false);
	const resource = searchParams.get("resource") ?? "";
	const api = useMemo(() => new MESApiService(), []);

	// Handle image upload for drag & drop and paste
	const onImageUpload = async (file: File): Promise<string> => {
		try {
			const res = await api.uploadFiles([file], "asset-text");
			if (res?.result?.[0]?.URL) {
				return res.result[0].URL;
			}
			setError("Failed to get image URL from upload response");
			return "";
		} catch (err) {
			setError("Failed to upload image");
			return "";
		}
	};

	const {
		state: {
			dataMarkDownText,
			assetSearchId,
			editMarkDownTextMood,
			workOrdersTable,
		},
		actions: { setDataMarkDownText, setEditMarkDownTextMood },
	} = useAssetDetailsContext();

	// Measure container and calculate editor height
	useEffect(() => {
		const updateHeight = () => {
			// When Work Orders is NOT visible, use more viewport height
			if (!workOrdersTable) {
				// Full height mode: viewport minus header/tabs/buttons (~280px)
				const fullHeight = window.innerHeight - 280;
				setEditorHeight(Math.max(fullHeight, 300));
			} else if (containerRef.current) {
				// With Work Orders: measure container
				const containerHeight = containerRef.current.clientHeight;
				const buttonAreaHeight = 60;
				const newHeight = Math.max(containerHeight - buttonAreaHeight, 150);
				setEditorHeight(newHeight);
			}
		};

		updateHeight();
		window.addEventListener("resize", updateHeight);
		const timer = setTimeout(updateHeight, 100);

		return () => {
			window.removeEventListener("resize", updateHeight);
			clearTimeout(timer);
		};
	}, [isLoading, workOrdersTable]);

	const searchMarkDownText = useSearchMarkDownText({ setIsLoading, setError });

	const handleCancel = () => {
		setEditMarkDownTextMood(false);
		searchMarkDownText();
	};

	const handleClose = (
		event: Event | React.SyntheticEvent,
		reason?: string,
	) => {
		if (reason === "clickaway") {
			return;
		}
		setEditMarkDownTextSuccess(false);
	};

	const handleSaveBtn = async () => {
		setIsSubmitLoading(true);
		const res = await upsertMarkDownText(assetSearchId, dataMarkDownText);
		if (res.ok) {
			searchMarkDownText();
			setIsSubmitLoading(false);
			setEditMarkDownTextSuccess(true);
			setEditMarkDownTextMood(false);
		} else {
			setUpdateMarkDownError(res.message);
			setIsSubmitLoading(false);
		}
	};

	useEffect(() => {
		if (resource === "text") {
			searchMarkDownText();
			setEditMarkDownTextMood(false);
		}
	}, [resource, assetSearchId]);

	return (
		<>
			{isLoading ? (
				<div
					style={{
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						width: "100%",
						height: "100%",
					}}
				>
					<LazyLoading />
				</div>
			) : (
				<div
					ref={containerRef}
					style={{
						height: "100%",
						width: "100%",
						display: "flex",
						flexDirection: "column",
					}}
				>
					{!editMarkDownTextMood ? (
						<>
							<div style={{ marginBottom: 8 }}>
								<MdEditor
									key="view-mode"
									style={{
										height: editorHeight,
										width: "100%",
										border: "1px solid #e0e0e0",
										borderRadius: 4,
									}}
									value={(dataMarkDownText as string) || ""}
									renderHTML={(text) => mdParser.render(text || "")}
									onChange={({ text }) => setDataMarkDownText(text)}
									config={{
										view: {
											menu: false,
											md: false,
											html: true,
										},
									}}
								/>
							</div>
							<Grid2
								container
								justifyContent="flex-end"
								spacing={1}
								sx={{ flexShrink: 0, py: 1 }}
							>
								<Grid2 size={{ xs: 6, sm: 3, md: 2 }}>
									<Button
										fullWidth
										variant="contained"
										color="primary"
										onClick={() => setEditMarkDownTextMood(true)}
									>
										EDIT
									</Button>
								</Grid2>
							</Grid2>
						</>
					) : (
						<>
							<div style={{ marginBottom: 8 }}>
								<MdEditor
									key="edit-mode"
									style={{
										height: editorHeight,
										width: "100%",
										border: "1px solid #e0e0e0",
										borderRadius: 4,
									}}
									value={(dataMarkDownText as string) || ""}
									renderHTML={(text) => mdParser.render(text)}
									onChange={({ text }) => setDataMarkDownText(text)}
									config={{
										view: {
											menu: true,
											md: true,
											html: false,
										},
									}}
									onImageUpload={onImageUpload}
								/>
							</div>
							<Grid2
								container
								justifyContent="flex-end"
								spacing={1}
								sx={{ flexShrink: 0, py: 1 }}
							>
								<Grid2 size={{ xs: 6, sm: 3, md: 2 }}>
									<Button
										fullWidth
										variant="contained"
										color="inherit"
										onClick={handleCancel}
									>
										CANCEL
									</Button>
								</Grid2>
								<Grid2 size={{ xs: 6, sm: 3, md: 2 }}>
									<Button
										fullWidth
										startIcon={
											isSubmitLoading && <CircularProgress size="1rem" />
										}
										disabled={isSubmitLoading}
										variant="contained"
										color="primary"
										onClick={handleSaveBtn}
									>
										SAVE
									</Button>
								</Grid2>
							</Grid2>
						</>
					)}
				</div>
			)}
			<Snackbar
				open={editMarkDownTextSuccess}
				autoHideDuration={2500}
				onClose={handleClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert severity="info" onClose={handleClose}>
					Text edited
				</Alert>
			</Snackbar>
			<ErrorModal error={error} onHide={() => setError("")} />
			<ErrorModal
				error={updateMarkDownerror}
				onHide={() => setUpdateMarkDownError("")}
			/>
		</>
	);
};
